<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue'
import {
  cleanAmountInput,
  formatAmountDisplay,
  nextCaretIndex,
  stripPendingSeparator,
  type AmountFormat,
} from './amount-input.ts'
import { evaluateAmountExpression, isAmountExpression } from './amount-expression.ts'

/**
 * A locale-grouped decimal text input: grouped as you type with the caret held on the digit
 * it started next to, a decimal point capped at `decimalDigits`, and an optional unit badge
 * inside the field. Plain strings in and out (never a domain type), for the numeric fields
 * that are not a currency amount and so cannot go through the `Money`-typed `AmountInput`
 * (apps/web/src/app/components/AmountInput.vue) - an interest rate, a target's ratio bound, a
 * cross-currency magnitude filter with no currency of its own. That component composes this
 * one internally for its own actual input rendering, rather than duplicating it.
 */
const props = withDefaults(
  defineProps<{
    modelValue: string
    /** The fraction this field accepts; 0 disables the decimal point entirely. */
    decimalDigits: number
    groupSeparator: string
    decimalSeparator: string
    /** Shown inside the field, e.g. '%'. */
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; no unit badge when unset
    unit?: string | undefined
    allowNegative?: boolean
    /** A live formatted echo below the field - off for a dense grid/table row. */
    showTotal?: boolean
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty id is a real attribute that can collide
    id?: string | undefined
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty aria-label suppresses the accessible name
    ariaLabel?: string | undefined
    required?: boolean
    disabled?: boolean
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty placeholder is a real attribute
    placeholder?: string | undefined
  }>(),
  { showTotal: true, allowNegative: false, required: false, disabled: false },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

defineOptions({ inheritAttrs: false })

const format = computed<AmountFormat>(() => ({
  groupSeparator: props.groupSeparator,
  decimalSeparator: props.decimalSeparator,
  decimalDigits: props.decimalDigits,
  allowNegative: props.allowNegative,
}))

let lastEmitted = props.modelValue
const clean = ref(props.modelValue)
/** Raw quick-math text while one is being typed; `clean` then holds its result, or ''. */
const expression = ref<string | undefined>(undefined)
const expressionInvalid = ref(false)
const attrs = useAttrs()
const ariaInvalid = computed(() =>
  expressionInvalid.value ? 'true' : (attrs['aria-invalid'] as boolean | 'true' | 'false' | undefined),
)

watch(
  () => props.modelValue,
  (value) => {
    if (value === lastEmitted) return
    clean.value = value
    expression.value = undefined
    expressionInvalid.value = false
  },
)

const formattedClean = computed(() => formatAmountDisplay(clean.value, format.value))
const display = computed(() => expression.value ?? formattedClean.value)

/**
 * A zero-decimal field never shows a decimal separator, so a typed one leaves nothing on screen
 * for the next keystroke to be read against: "12" + "." + "5" would otherwise show "125". This
 * remembers that a decimal point was typed, and drops the digits typed after it - they are
 * fraction digits the currency has no room for. Deleting a character clears it again.
 */
let fractionTyped = false

function emitClean(value: string): void {
  if (value === lastEmitted) return
  lastEmitted = value
  emit('update:modelValue', value)
}

function onExpressionInput(raw: string): void {
  fractionTyped = false
  expression.value = raw
  expressionInvalid.value = false
  clean.value = evaluateAmountExpression(raw, format.value) ?? ''
  emitClean(clean.value)
}

/** Blur or Enter: a valid expression becomes its result; an invalid one stays, marked invalid. */
function commitExpression(): void {
  if (expression.value === undefined) return
  const result = evaluateAmountExpression(expression.value, format.value)
  if (result === undefined) {
    expressionInvalid.value = true
    return
  }
  expression.value = undefined
  clean.value = result
  emitClean(result)
}

function onInput(event: Event): void {
  const target = event.target as HTMLInputElement
  const raw = target.value
  const caret = target.selectionStart ?? raw.length
  if (isAmountExpression(raw)) {
    onExpressionInput(raw)
    return
  }
  expression.value = undefined
  expressionInvalid.value = false
  const pasted =
    (event as InputEvent).inputType === 'insertFromPaste' ||
    (event as InputEvent).inputType === 'insertFromDrop'
  if (!pasted && format.value.decimalDigits === 0) {
    if (raw.length < display.value.length) {
      fractionTyped = false
    } else if (fractionTyped) {
      target.value = display.value
      target.setSelectionRange(display.value.length, display.value.length)
      return
    } else {
      fractionTyped = [...raw].some((ch) => (ch === '.' || ch === ',') && ch !== format.value.groupSeparator)
    }
  }
  const nextClean = cleanAmountInput(raw, format.value, pasted)
  const formatted = formatAmountDisplay(nextClean, format.value)
  const caretIndex = nextCaretIndex(raw, caret, formatted)
  clean.value = nextClean
  target.value = formatted
  target.setSelectionRange(caretIndex, caretIndex)
  const emitted = stripPendingSeparator(nextClean)
  lastEmitted = emitted
  emit('update:modelValue', emitted)
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <div class="relative flex items-center">
      <input
        v-bind="$attrs"
        :id="id"
        type="text"
        inputmode="decimal"
        autocomplete="off"
        :value="display"
        :aria-label="ariaLabel"
        :required="required"
        :disabled="disabled"
        :placeholder="placeholder"
        :aria-invalid="ariaInvalid"
        class="h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-right text-[color:var(--color-text)] tabular-nums"
        :class="unit ? 'pr-10' : undefined"
        :style="{ fontFamily: 'var(--font-numeric)' }"
        @input="onInput"
        @blur="commitExpression"
        @keydown.enter="commitExpression"
      />
      <span
        v-if="unit"
        aria-hidden="true"
        class="pointer-events-none absolute right-[var(--space-cell)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
        >{{ unit }}</span
      >
    </div>
    <p
      v-if="showTotal && clean !== ''"
      aria-hidden="true"
      class="text-[length:var(--text-step-1)] tabular-nums text-[color:var(--color-text-muted)]"
      :style="{ fontFamily: 'var(--font-numeric)' }"
    >
      {{ formattedClean }}<template v-if="unit">{{ ' ' }}{{ unit }}</template>
    </p>
  </div>
</template>
